import { create } from "zustand";
import {
  CommentTargetType,
  WorkspaceComment,
} from "@/models/workspace/WorkspaceComment";
import { User } from "@/models/users/User";

type CommentsStore = {
  comments: WorkspaceComment[];
  setComments: (comments: WorkspaceComment[]) => void;
  replyingTo: WorkspaceComment | undefined;
  setReplyingTo: (replyingTo: WorkspaceComment | undefined) => void;
  deletingCommentIds: Set<string>;
  setDeletingCommentIds: (commentIds: Set<string>) => void;
  markCommentForDeletion: (comment: WorkspaceComment) => void;
  clearDeletingComments: () => void;
  expandedReplies: Record<string, boolean>;
  setExpandedReplies: (expandedReplies: Record<string, boolean>) => void;
  tenantUsers: User[];
  setTenantUsers: (tenantUsers: User[]) => void;
  hoveredThread: string | null;
  setHoveredThread: (hoveredThread: string | null) => void;

  filter: string;
  setFilter: (filter: string) => void;
  prepareToReply: (comment: WorkspaceComment | undefined) => void;
  openReplies: (commentId: string) => void;
  closeReplies: (commentId: string) => void;
  toggleReplies: (commentId: string) => void;
  getCommentAuthor: (comment: WorkspaceComment) => User | undefined;
  getReactorById: (userId: string) => User | undefined;
  reset: () => void;
};

const initialState = {
  comments: [],
  replyingTo: undefined,
  deletingCommentIds: new Set<string>(),
  expandedReplies: {},
  tenantUsers: [],
  hoveredThread: null,
  filter: "workspace",
};

const useCommentsStore = create<CommentsStore>((set, get) => ({
  ...initialState,

  setComments: (comments) => set({ comments }),
  setReplyingTo: (replyingTo) => set({ replyingTo }),
  setDeletingCommentIds: (deletingCommentIds) => set({ deletingCommentIds }),

  markCommentForDeletion: (comment: WorkspaceComment) => {
    set((state) => {
      const deletingCommentIds = new Set(state.deletingCommentIds);

      // Helper function to recursively add comment and all nested replies
      const addCommentAndReplies = (commentToAdd: WorkspaceComment) => {
        deletingCommentIds.add(commentToAdd.id);

        // Recursively add all replies and their nested replies
        if (commentToAdd.replies && commentToAdd.replies.length > 0) {
          commentToAdd.replies.forEach((reply) => {
            addCommentAndReplies(reply);
          });
        }
      };

      // Start the recursive process with the initial comment
      addCommentAndReplies(comment);

      return { deletingCommentIds };
    });
  },

  clearDeletingComments: () => {
    set({ deletingCommentIds: new Set<string>() });
  },

  setExpandedReplies: (expandedReplies) => set({ expandedReplies }),
  setTenantUsers: (tenantUsers) => set({ tenantUsers }),
  setHoveredThread: (hoveredThread) => set({ hoveredThread }),
  setFilter: (filter) => set({ filter }),
  openReplies: (commentId: string) => {
    set((state) => {
      const expandedReplies = state.expandedReplies;
      return {
        expandedReplies: {
          ...expandedReplies,
          [commentId]: true,
        },
      };
    });
  },

  closeReplies: (commentId: string) => {
    set((state) => {
      const expandedReplies = state.expandedReplies;
      return {
        expandedReplies: {
          ...expandedReplies,
          [commentId]: false,
        },
      };
    });
  },

  toggleReplies: (commentId: string) => {
    set((state) => {
      const expandedReplies = state.expandedReplies;
      return {
        expandedReplies: {
          ...expandedReplies,
          [commentId]: !expandedReplies[commentId],
        },
      };
    });
  },

  prepareToReply: (comment: WorkspaceComment | undefined) => {
    if (comment) {
      set((state) => {
        const expandedReplies = state.expandedReplies;

        if (
          comment.targetType === CommentTargetType.Workspace ||
          comment.targetType === CommentTargetType.File
        ) {
          expandedReplies[comment.id] = true;

          comment.replies?.forEach((reply) => {
            expandedReplies[reply.id] = false;
          });
        } else {
          expandedReplies[comment.id] = true;
        }

        return {
          replyingTo: comment,
          expandedReplies: {
            ...expandedReplies,
          },
        };
      });
    }
  },

  getCommentAuthor: (comment) => {
    return get().tenantUsers.find((user) => user.id === comment.userId);
  },

  getReactorById: (userId) => {
    return get().tenantUsers.find((user) => user.id === userId);
  },
  reset: () => set(initialState),
}));

export default useCommentsStore;
